/**
 * Copyright (c) 2025 Stefan Radig
 * Unified username service for authenticated and non-authenticated users
 */

import { AuthManager } from './auth.js';

export class UsernameService {
    private authManager: AuthManager;
    private static readonly STORAGE_KEY = 'graph-editor-user-name';

    constructor(authManager: AuthManager) {
        this.authManager = authManager;
    }

    /**
     * Get the current username
     * - If authenticated: returns token_name from JWT
     * - If not authenticated: returns generated name from localStorage
     */
    public getUserName(): string {
        // Check if user is authenticated
        const tokenName = this.authManager.getTokenName();
        if (tokenName) {
            return tokenName;
        }

        // Not authenticated - use generated name
        return this.getOrGenerateName();
    }

    /**
     * Get or generate a random username for non-authenticated users
     */
    private getOrGenerateName(): string {
        const existingName = localStorage.getItem(UsernameService.STORAGE_KEY);
        if (existingName) {
            return existingName;
        }

        // Generate new name
        const newName = this.generateRandomName();
        localStorage.setItem(UsernameService.STORAGE_KEY, newName);
        return newName;
    }

    /**
     * Generate a random username in the format: AdjectiveNounNumber
     */
    private generateRandomName(): string {
        const adjectives = [
            'Swift', 'Clever', 'Bright', 'Bold', 'Calm', 'Eager',
            'Fierce', 'Gentle', 'Happy', 'Jolly', 'Kind', 'Lively',
            'Mighty', 'Noble', 'Quick', 'Silent', 'Wise', 'Brave',
            'Cosmic', 'Electric', 'Mystic', 'Sonic', 'Stellar', 'Turbo'
        ];

        const nouns = [
            'Cat', 'Dog', 'Bird', 'Fox', 'Bear', 'Wolf',
            'Tiger', 'Lion', 'Eagle', 'Hawk', 'Owl', 'Panda',
            'Dragon', 'Phoenix', 'Falcon', 'Raven', 'Lynx', 'Otter',
            'Comet', 'Nova', 'Pulsar', 'Quasar', 'Star', 'Nebula'
        ];

        const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        const number = Math.floor(Math.random() * 100);

        return `${adjective}${noun}${number}`;
    }

    /**
     * Clear the generated username (useful for testing)
     */
    public clearGeneratedName(): void {
        localStorage.removeItem(UsernameService.STORAGE_KEY);
    }

    /**
     * Check if user is using an authenticated name (token name)
     */
    public isUsingTokenName(): boolean {
        return this.authManager.getTokenName() !== null;
    }
}
